import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Visibility,
  Block,
  CheckCircle,
  Edit,
  Refresh,
  Search,
  FilterList,
  Clear,
  People,
  TrendingUp,
  AccountBalanceWallet,
  Payment,
} from '@mui/icons-material';
import CustomPagination from '../../components/common/CustomPagination';
import {
  fetchAffiliatePartners,
  suspendAffiliate,
  activateAffiliate,
  updateAffiliateCommission,
  bulkSuspendAffiliates,
  bulkActivateAffiliates,
  fetchAffiliateStatistics,
  fetchAffiliateCommissions,
  fetchAffiliateSettings,
  updateAffiliateSettings,
  manualDisburseFunds,
  toggleAutoDisburse,
  getDisburseStatus,
} from '../../services/affiliate';

const AffiliateAdminPage = () => {
  const { t } = useTranslation(['affiliate', 'common']);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Statistics
  const [statistics, setStatistics] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
  });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  
  // Modals
  const [detailModal, setDetailModal] = useState({ open: false, affiliate: null });
  const [commissionModal, setCommissionModal] = useState({ open: false, affiliate: null, commission: '' });
  const [bulkActionModal, setBulkActionModal] = useState({ open: false, action: '', selectedIds: [] });
  const [settingsModal, setSettingsModal] = useState(false);
  
  // Settings
  const [settings, setSettings] = useState({ default_commission_percentage: 25, terms_url: '', disburse_interval_days: 15 });
  const [settingsForm, setSettingsForm] = useState({ default_commission_percentage: 25, terms_url: '', disburse_interval_days: 15 });
  
  // Selected rows for bulk actions
  const [selectedRows, setSelectedRows] = useState([]);
  
  // Commission data for detail modal
  const [commissionData, setCommissionData] = useState({ commissions: [], summary: {} });
  const [loadingCommissions, setLoadingCommissions] = useState(false);
  
  // Disbursement state
  const [disburseStatus, setDisburseStatus] = useState(null);
  const [loadingDisburseStatus, setLoadingDisburseStatus] = useState(false);
  const [disbursing, setDisbursing] = useState(false);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  // Debounce hook
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      
      return () => clearTimeout(handler);
    }, [value, delay]);
    
    return debouncedValue;
  };

  const debouncedSearch = useDebounce(filters.search, 500);
  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  // Fetch affiliates
  const fetchAffiliates = useCallback(async () => {
      try {
        setLoading(true);
      const params = {
        page: page + 1,
        page_size: pageSize,
      };
      
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      
      if (filters.status) {
        params.status = filters.status;
      }
      
      const response = await fetchAffiliatePartners(params);
      
      if (response.results) {
        setAffiliates(response.results);
        setTotalCount(response.count || response.results.length);
      } else {
        setAffiliates(Array.isArray(response) ? response : []);
        setTotalCount(Array.isArray(response) ? response.length : 0);
      }
      } catch (error) {
        setSnackbar({
          open: true,
          severity: 'error',
        message: error?.response?.data?.detail || t('affiliate:messages.failedToLoad'),
        });
      } finally {
        setLoading(false);
      }
  }, [page, pageSize, debouncedSearch, filters.status, t]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const stats = await fetchAffiliateStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  }, []);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const data = await fetchAffiliateSettings();
      setSettings(data);
      setSettingsForm(data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, []);

  // Fetch disbursement status
  const fetchDisburseStatus = useCallback(async () => {
    try {
      setLoadingDisburseStatus(true);
      const status = await getDisburseStatus();
      setDisburseStatus(status);
    } catch (error) {
      console.error('Failed to fetch disbursement status:', error);
    } finally {
      setLoadingDisburseStatus(false);
    }
  }, []);

  useEffect(() => {
    fetchAffiliates();
  }, [fetchAffiliates]);

  useEffect(() => {
    fetchStats();
    fetchSettings();
    fetchDisburseStatus();
  }, [fetchStats, fetchSettings, fetchDisburseStatus]);

  // Handle manual disbursement
  const handleManualDisburse = async () => {
    try {
      setDisbursing(true);
      const result = await manualDisburseFunds();
      setSnackbar({
        open: true,
        severity: result.success ? 'success' : 'error',
        message: result.success
          ? `Disbursed ₦${result.total_amount_naira?.toLocaleString('en-NG', { minimumFractionDigits: 2 })} to ${result.partners_credited} partner(s)`
          : result.message || 'Disbursement failed',
      });
      if (result.success) {
        fetchDisburseStatus();
        fetchStats();
        fetchAffiliates();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.message || 'Failed to disburse funds',
      });
    } finally {
      setDisbursing(false);
    }
  };

  // Handle toggle auto-disburse
  const handleToggleAutoDisburse = async () => {
    try {
      const newValue = !disburseStatus?.auto_disburse_enabled;
      await toggleAutoDisburse(newValue);
      setSnackbar({
        open: true,
        severity: 'success',
        message: `Auto-disburse ${newValue ? 'enabled' : 'disabled'}`,
      });
      fetchDisburseStatus();
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.message || 'Failed to update auto-disburse setting',
      });
    }
  };

  const handleRowsPerPageChange = (event) => {
    const value = event?.target?.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      return;
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && numericValue > 0) {
      setPageSize(numericValue);
      setPage(0);
    }
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput, 10);
    if (value && value > 0 && value <= 1000) {
      setPageSize(value);
      setPage(0);
    }
    setCustomRowsInput('');
    setCustomRowsActive(false);
  };

  // Handle suspend
  const handleSuspend = useCallback(async (affiliateId) => {
    try {
      await suspendAffiliate(affiliateId);
      setSnackbar({
        open: true,
        severity: 'success',
        message: t('affiliate:messages.suspendedSuccess'),
      });
      fetchAffiliates();
      fetchStats();
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.detail || t('affiliate:messages.suspendFailed'),
      });
    }
  }, [fetchAffiliates, fetchStats, t]);

  // Handle activate
  const handleActivate = useCallback(async (affiliateId) => {
    try {
      await activateAffiliate(affiliateId);
      setSnackbar({
        open: true,
        severity: 'success',
        message: t('affiliate:messages.activatedSuccess'),
      });
      fetchAffiliates();
      fetchStats();
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.detail || t('affiliate:messages.activateFailed'),
      });
    }
  }, [fetchAffiliates, fetchStats, t]);

  // Handle update commission
  const handleUpdateCommission = async () => {
    try {
      await updateAffiliateCommission(commissionModal.affiliate.id, parseFloat(commissionModal.commission));
      setSnackbar({
        open: true,
        severity: 'success',
        message: t('affiliate:messages.commissionUpdated'),
      });
      setCommissionModal({ open: false, affiliate: null, commission: '' });
      fetchAffiliates();
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.detail || t('affiliate:messages.commissionUpdateFailed'),
      });
    }
  };

  // Handle bulk actions
  const handleBulkAction = async () => {
    try {
      if (bulkActionModal.action === 'suspend') {
        await bulkSuspendAffiliates(bulkActionModal.selectedIds);
        setSnackbar({
          open: true,
          severity: 'success',
          message: t('affiliate:messages.bulkSuspendedSuccess', { count: bulkActionModal.selectedIds.length }),
        });
      } else if (bulkActionModal.action === 'activate') {
        await bulkActivateAffiliates(bulkActionModal.selectedIds);
        setSnackbar({
          open: true,
          severity: 'success',
          message: t('affiliate:messages.bulkActivatedSuccess', { count: bulkActionModal.selectedIds.length }),
        });
      }
      setBulkActionModal({ open: false, action: '', selectedIds: [] });
      setSelectedRows([]);
      fetchAffiliates();
      fetchStats();
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.detail || t('affiliate:messages.bulkActionFailed'),
      });
    }
  };

  // Handle view details
  const handleViewDetails = async (affiliate) => {
    setDetailModal({ open: true, affiliate });
    try {
      setLoadingCommissions(true);
      const data = await fetchAffiliateCommissions(affiliate.id);
      setCommissionData(data);
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    } finally {
      setLoadingCommissions(false);
    }
  };

  // Handle save settings
  const handleSaveSettings = async () => {
    try {
      await updateAffiliateSettings(settingsForm);
      setSettings(settingsForm);
      setSnackbar({
        open: true,
        severity: 'success',
        message: t('affiliate:messages.settingsUpdatedSuccess'),
      });
      setSettingsModal(false);
      fetchDisburseStatus();
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.detail || t('affiliate:messages.unableToUpdate'),
      });
    }
  };

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'id',
      headerName: t('affiliate:table.sn'),
      width: 60,
      renderCell: (params) => {
        const index = page * pageSize + params.api.getRowIndexRelativeToVisibleRows(params.id) + 1;
        return index;
      },
    },
    {
      field: 'username',
      headerName: t('affiliate:table.affiliate'),
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ width: '100%', py: 0.5 }}>
          <Typography 
            variant="body2" 
            fontWeight="bold"
            sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mb: 0.25
            }}
          >
            {params.row.username}
          </Typography>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
          >
            {params.row.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'referral_code',
      headerName: t('affiliate:table.referralCode'),
      width: 150,
    },
    {
      field: 'status',
      headerName: t('affiliate:table.status'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status === 'active' ? t('affiliate:status.active') : t('affiliate:status.inactive')}
          color={params.row.status === 'active' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'commission_percentage',
      headerName: t('affiliate:table.commissionRate'),
      width: 150,
      renderCell: (params) => `${params.row.commission_percentage}%`,
    },
    {
      field: 'total_rewards',
      headerName: t('affiliate:table.totalCommissions'),
      width: 150,
      renderCell: (params) => `¥${parseFloat(params.row.total_rewards || 0).toFixed(2)}`,
    },
    {
      field: 'pending_rewards',
      headerName: t('affiliate:table.pendingCommissions'),
      width: 150,
      renderCell: (params) => `¥${parseFloat(params.row.pending_rewards || 0).toFixed(2)}`,
    },
    {
      field: 'total_referrals',
      headerName: t('affiliate:table.totalReferrals'),
      width: 120,
    },
    {
      field: 'joined_at',
      headerName: t('affiliate:table.joinedDate'),
      width: 150,
      renderCell: (params) => new Date(params.row.joined_at).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: t('affiliate:table.actions'),
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('affiliate:actions.viewDetails')}>
            <IconButton size="small" onClick={() => handleViewDetails(params.row)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'active' ? (
            <Tooltip title={t('affiliate:actions.suspend')}>
              <IconButton size="small" onClick={() => handleSuspend(params.row.id)} color="error">
                <Block fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={t('affiliate:actions.activate')}>
              <IconButton size="small" onClick={() => handleActivate(params.row.id)} color="success">
                <CheckCircle fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('affiliate:actions.editCommission')}>
            <IconButton
              size="small"
              onClick={() => setCommissionModal({ open: true, affiliate: params.row, commission: params.row.commission_percentage })}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [handleActivate, handleSuspend, page, pageSize, t]);

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {t('affiliate:title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('affiliate:description')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setSettingsModal(true)}
          >
            {t('affiliate:actions.settings')}
          </Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchAffiliates}>
            {t('common:refresh')}
          </Button>
        </Box>
      </Box>

      {/* Fund Disbursement Section */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: 'primary.light', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {t('affiliate:disbursement.title')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {t('affiliate:disbursement.description')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{t('affiliate:disbursement.autoDisburse')}</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={disburseStatus?.auto_disburse_enabled || false}
                    onChange={handleToggleAutoDisburse}
                    disabled={loadingDisburseStatus}
                  />
                }
                label=""
              />
            </Box>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Payment />}
              onClick={handleManualDisburse}
              disabled={disbursing || loadingDisburseStatus}
            >
              {disbursing ? t('affiliate:disbursement.disbursing') : t('affiliate:disbursement.disburseNow')}
            </Button>
          </Box>
        </Box>
        
        {loadingDisburseStatus ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} sx={{ color: 'white' }} />
          </Box>
        ) : disburseStatus && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {t('affiliate:disbursement.pendingAmount')}
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  ₦{parseFloat(disburseStatus.pending_amount_naira || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {disburseStatus.pending_count || 0} {t('affiliate:disbursement.rewards')}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {t('affiliate:disbursement.lastDisburse')}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {disburseStatus.last_disburse_date
                    ? new Date(disburseStatus.last_disburse_date).toLocaleDateString()
                    : t('affiliate:disbursement.never')}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {t('affiliate:disbursement.nextDisburse')}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {disburseStatus.next_disburse_date
                    ? new Date(disburseStatus.next_disburse_date).toLocaleDateString()
                    : t('affiliate:disbursement.notScheduled')}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {t('affiliate:disbursement.status')}
                </Typography>
                <Chip
                  label={disburseStatus.auto_disburse_enabled ? t('affiliate:disbursement.enabled') : t('affiliate:disbursement.disabled')}
                  color={disburseStatus.auto_disburse_enabled ? 'success' : 'default'}
                  size="small"
                  sx={{ backgroundColor: 'white', color: 'primary.main' }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Auto interval: every {disburseStatus.disburse_interval_days || 15} day(s)
              </Typography>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('affiliate:statistics.totalAffiliates')}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {statistics.total_affiliates}
                    </Typography>
                  </Box>
                  <People color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('affiliate:statistics.activeAffiliates')}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {statistics.active_affiliates}
                    </Typography>
                  </Box>
                  <CheckCircle color="success" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('affiliate:statistics.totalCommissions')}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      ¥{parseFloat(statistics.total_commissions || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <TrendingUp color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('affiliate:statistics.pendingCommissions')}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="warning.main">
                      ¥{parseFloat(statistics.pending_commissions || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <AccountBalanceWallet color="warning" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('affiliate:filters.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: filters.search && (
                  <IconButton size="small" onClick={() => setFilters({ ...filters, search: '' })}>
                    <Clear fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('affiliate:filters.status')}</InputLabel>
              <Select
                value={filters.status}
                label={t('affiliate:filters.status')}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="">{t('common:all')}</MenuItem>
                <MenuItem value="active">{t('affiliate:status.active')}</MenuItem>
                <MenuItem value="inactive">{t('affiliate:status.inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {selectedRows.length > 0 && (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Block />}
                    onClick={() => setBulkActionModal({ open: true, action: 'suspend', selectedIds: selectedRows })}
                  >
                    {t('affiliate:actions.bulkSuspend')} ({selectedRows.length})
                  </Button>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => setBulkActionModal({ open: true, action: 'activate', selectedIds: selectedRows })}
                  >
                    {t('affiliate:actions.bulkActivate')} ({selectedRows.length})
                  </Button>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* DataGrid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={affiliates}
          columns={columns}
          loading={loading}
          hideFooter
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          rowCount={totalCount}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          checkboxSelection
          onSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          disableSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: theme => theme.palette.primary.main,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.875rem', sm: '1rem' }
            },
            '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
              color: 'white'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#F3F4F6'
            },
            '& .MuiDataGrid-cell': {
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '8px 4px', sm: '8px 12px' }
            }
          }}
        />
      </Paper>
      <CustomPagination
        page={page}
        totalPages={totalPages}
        rowsPerPage={pageSize}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        customRowsActive={customRowsActive}
        customRowsInput={customRowsInput}
        onCustomRowsInputChange={(event) => setCustomRowsInput(event.target.value)}
        onCustomRowsInputBlur={handleCustomRowsInputBlur}
      />

      {/* Detail Modal */}
      <Dialog
        open={detailModal.open}
        onClose={() => setDetailModal({ open: false, affiliate: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('affiliate:modals.detailTitle')} - {detailModal.affiliate?.username}
        </DialogTitle>
        <DialogContent>
          {detailModal.affiliate && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('affiliate:modals.email')}
                  </Typography>
                  <Typography variant="body1">{detailModal.affiliate.email}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('affiliate:modals.fullName')}
                  </Typography>
                  <Typography variant="body1">{detailModal.affiliate.full_name || '—'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('affiliate:modals.referralCode')}
                  </Typography>
                  <Typography variant="body1">{detailModal.affiliate.referral_code}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('affiliate:modals.status')}
                  </Typography>
                  <Chip
                    label={detailModal.affiliate.status === 'active' ? t('affiliate:status.active') : t('affiliate:status.inactive')}
                    color={detailModal.affiliate.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('affiliate:modals.commissionRate')}
                  </Typography>
                  <Typography variant="body1">{detailModal.affiliate.commission_percentage}%</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('affiliate:modals.joinedDate')}
                  </Typography>
                  <Typography variant="body1">
                    {new Date(detailModal.affiliate.joined_at).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('affiliate:modals.statistics')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('affiliate:modals.totalRewards')}
                  </Typography>
                  <Typography variant="h6">¥{parseFloat(detailModal.affiliate.total_rewards || 0).toFixed(2)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('affiliate:modals.pendingRewards')}
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    ¥{parseFloat(detailModal.affiliate.pending_rewards || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('affiliate:modals.approvedRewards')}
                  </Typography>
                  <Typography variant="h6" color="info.main">
                    ¥{parseFloat(detailModal.affiliate.approved_rewards || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('affiliate:modals.paidRewards')}
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    ¥{parseFloat(detailModal.affiliate.paid_rewards || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('affiliate:modals.totalReferrals')}
                  </Typography>
                  <Typography variant="h6">{detailModal.affiliate.total_referrals}</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('affiliate:modals.activeReferrals')}
                  </Typography>
                  <Typography variant="h6">{detailModal.affiliate.active_referrals}</Typography>
            </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('affiliate:modals.commissionHistory')}
                  </Typography>
                  {loadingCommissions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                            <TableCell>{t('affiliate:modals.orderNumber')}</TableCell>
                            <TableCell>{t('affiliate:modals.amount')}</TableCell>
                            <TableCell>{t('affiliate:modals.status')}</TableCell>
                            <TableCell>{t('affiliate:modals.date')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                          {commissionData.commissions && commissionData.commissions.length > 0 ? (
                            commissionData.commissions.slice(0, 10).map((commission) => (
                              <TableRow key={commission.id}>
                                <TableCell>{commission.order_number}</TableCell>
                                <TableCell>¥{parseFloat(commission.reward_amount || 0).toFixed(2)}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={commission.status}
                                    size="small"
                                    color={
                                      commission.status === 'paid' ? 'success' :
                                      commission.status === 'approved' ? 'info' :
                                      commission.status === 'pending' ? 'warning' : 'default'
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  {new Date(commission.calculated_at).toLocaleDateString()}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                                {t('affiliate:modals.noCommissions')}
                            </TableCell>
                            </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  )}
            </Grid>
          </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailModal({ open: false, affiliate: null })}>
            {t('common:close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Commission Update Modal */}
      <Dialog
        open={commissionModal.open}
        onClose={() => setCommissionModal({ open: false, affiliate: null, commission: '' })}
      >
        <DialogTitle>{t('affiliate:modals.updateCommission')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label={t('affiliate:modals.commissionPercentage')}
            value={commissionModal.commission}
            onChange={(e) => setCommissionModal({ ...commissionModal, commission: e.target.value })}
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommissionModal({ open: false, affiliate: null, commission: '' })}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleUpdateCommission} variant="contained">
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Modal */}
      <Dialog
        open={bulkActionModal.open}
        onClose={() => setBulkActionModal({ open: false, action: '', selectedIds: [] })}
      >
        <DialogTitle>
          {bulkActionModal.action === 'suspend'
            ? t('affiliate:modals.bulkSuspendTitle')
            : t('affiliate:modals.bulkActivateTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {bulkActionModal.action === 'suspend'
              ? t('affiliate:modals.bulkSuspendMessage', { count: bulkActionModal.selectedIds.length })
              : t('affiliate:modals.bulkActivateMessage', { count: bulkActionModal.selectedIds.length })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionModal({ open: false, action: '', selectedIds: [] })}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleBulkAction} variant="contained" color={bulkActionModal.action === 'suspend' ? 'error' : 'success'}>
            {t('common:confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={settingsModal} onClose={() => setSettingsModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('affiliate:commissionSettings.title')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('affiliate:commissionSettings.defaultCommission')}
            type="number"
            value={settingsForm.default_commission_percentage}
            onChange={(e) => setSettingsForm({ ...settingsForm, default_commission_percentage: e.target.value })}
            inputProps={{ min: 0, max: 100, step: 0.5 }}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Auto disbursement interval (days)"
            type="number"
            value={settingsForm.disburse_interval_days || 15}
            onChange={(e) => setSettingsForm({ ...settingsForm, disburse_interval_days: e.target.value })}
            inputProps={{ min: 1, step: 1 }}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t('affiliate:commissionSettings.termsUrl')}
            value={settingsForm.terms_url || ''}
            onChange={(e) => setSettingsForm({ ...settingsForm, terms_url: e.target.value })}
            placeholder={t('affiliate:commissionSettings.termsUrlPlaceholder')}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSettingsModal(false);
            setSettingsForm(settings);
          }}>
            {t('common:cancel')}
          </Button>
          <Button onClick={handleSaveSettings} variant="contained">
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AffiliateAdminPage;
